import React from 'react';
import { AlertCircle } from 'react-feather';

/**
 * Renders the dynamic concessionaire charges grid (1–5 fields).
 *
 * Charges definitions come from `useTenantLookups.charges` —
 * `[{other_charge_id, display_name, charge_name, slot_index}, ...]`.
 *
 * Values are written into the parent form state under `tenant_charge1`
 * through `tenant_charge5`, bound by **`slot_index`** — the server-assigned
 * slot, not array position.
 *
 * Position used to be the binding, which meant unassigning a charge shifted
 * every later one up and silently relabelled money already stored against
 * tenants. `slot_index` snapshots the historical positional order once and
 * then holds it fixed, so a removal now leaves a gap instead of a shift.
 *
 * Do not bind by `charge_name`: those catalog values are literally
 * "charge1".."charge5" and look authoritative, but the stored amounts do not
 * follow them (see concessionaireChargesController.loadRowsWithSlots).
 */
const ChargesFieldset = ({ charges = [], values = {}, errors = {}, onChange, disabled = false }) => {
    if (!charges.length) return null;
    return (
        <>
            {charges.map((charge, idx) => {
                // Fallback to position only for a server that predates slot_index.
                const slot = charge.slot_index || idx + 1;
                const fieldName = `tenant_charge${slot}`;
                const v = values[fieldName];
                return (
                    <label key={charge.other_charge_id || idx} className="v3-field">
                        <span className="v3-label">{charge.display_name}</span>
                        <input
                            className="v3-input"
                            type="number"
                            step="0.001"
                            value={v ?? ''}
                            onChange={(e) => onChange(fieldName, e.target.value)}
                            placeholder="0"
                            disabled={disabled}
                        />
                        {errors[fieldName] && (
                            <span className="v3-sadmin-field-err">
                                <AlertCircle size={12} /> {errors[fieldName]}
                            </span>
                        )}
                    </label>
                );
            })}
        </>
    );
};

export default ChargesFieldset;
